import { Component, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, inject, input, output } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { EMPTY, switchMap } from 'rxjs';
import { ERROR_MESSAGE } from 'src/app/_helpers/utils';
import { FormBase } from 'src/app/_models/form-base';
import { ActiveModulesComponent } from '../../chips/active-modules/active-modules.component';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'app-checkbox-group',
    templateUrl: './checkbox-group.component.html',
    styleUrls: ['./checkbox-group.component.scss'],
    imports: [FormsModule, ReactiveFormsModule, MatIcon, ActiveModulesComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckboxGroupComponent {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  title: 'Checkbox';
  component: '';

  /** Checkbox base field (required) */
  field = input<FormBase<string>>();

  /** Parent FormGroup containing the control (required) */
  parentForm = input<FormGroup>();

  private readonly formSyncSubscription = toObservable(this.parentForm)
    .pipe(
      switchMap((form) => (form ? form.valueChanges : EMPTY)),
      takeUntilDestroyed(this.destroyRef),
    )
    .subscribe(() => this.cdr.markForCheck());

  /** Set whether checkboxes are disabled (optional, default: false) */
  isDisabled = input<boolean>(false);

  /** Set checkbox style (optional, default: 'default') */
  checkboxStyle = input<'default' | 'tabs' | 'tabs-small' | 'list'>('default');

  /** Whether to show module icon (optional) */
  showModuleIcon = input<string | undefined>(undefined);

  /** Emitted when a checkbox is clicked */
  checkboxClicked = output<Event>();

  updateValue(event: Event): void {
    this.checkboxClicked.emit(event);
  }

  /**
   * Gets the value of the checkbox's corresponding form control
   * @returns {boolean} The checkbox value
   */
  getChecked(index: number): boolean {
    const controlAccessor = this.field().key;
    const arrayKey = 'controls';
    const arrayIndex = index;
    return this.parentForm()?.controls?.[controlAccessor]?.[arrayKey]?.[arrayIndex]
      ?.value as boolean;
  }

  public get hasErrors(): boolean {
    return (
      !!this.parentForm()?.controls?.[this.field().key] &&
      !!this.parentForm()?.controls?.[this.field().key]?.errors &&
      !!this.parentForm()?.controls?.[this.field().key]?.touched
    );
  }

  public get errorsList(): string[] {
    const errors = this.parentForm()?.controls?.[this.field().key]?.errors ?? {};
    return Object.keys(errors).map((err) =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      (ERROR_MESSAGE as any)[err](
        this.parentForm()?.controls?.[this.field().key]?.getError(err),
      ) as string,
    );
  }
}
